import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { PLAYBOOKS_URL } from '../config/api-config';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export interface ExecutionRecord {
  // Core identifiers
  playbookId: string;
  playbookName: string;
  executionId: string;
  executionDate: string;
  
  // Execution status and results
  status?: string;
  result?: any;
  resultType?: string;
  error?: string;
  errorType?: string;
  
  // Performance metrics
  duration?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  avgTokensPerConversation?: number;
  estimatedCost?: number;
  
  // Interaction metrics
  conversationCount?: number;
  errorCount?: number;
  toolInvocationCount?: number;
  
  // Team composition
  toolsUsed?: string[];
  toolsCount?: number;
  agentsInvolved?: string[];
  agentsCount?: number;
  modelsUsed?: string[];
  
  // Input analysis
  inputDataSummary?: string;
  inputType?: string;
  inputComplexity?: 'Low' | 'Medium' | 'High';
  
  // Playbook configuration
  playbookDescription?: string;
  playbookWorkflow?: string;
  approvalNotes?: string;
  specialInstructions?: string;
  aiModel?: string;
  maxTurns?: number;
  
  // User context
  userId?: string;
  ownerId?: string;
  environment?: string;
  
  // Reasoning and analysis
  reasoningSteps?: string;
  reasoningStepsCount?: number;
  
  // AI Summary
  aiSummary?: string;

  // Logs
  aiLog?: string;
  executionLog?: string;
  
  // Time-based querying
  executionYear?: string;
  executionMonth?: string;
  executionDay?: string;
  executionHour?: number;
}

export interface ExecutionRecordsResponse {
  items: ExecutionRecord[];
  metadata: {
    count: number;
    scannedCount: number;
    lastEvaluatedKey: string | null;
    pageNumber: number;
  };
}

export const useExecutionRecordsApi = () => {
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getExecutionRecords = async (
    playbookId: string = '',
    pageSize: number = 10,
    pageNumber: number = 1,
    filters?: {
      playbookName?: string;
      executionId?: string;
      ownerId?: string;
      status?: string;
      dateFrom?: string;
      dateTo?: string;
    },
    ownerId: string = ''
  ): Promise<ExecutionRecordsResponse> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${PLAYBOOKS_URL}/execution-records`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            playbookId,
            ownerId,
            pageSize,
            pageNumber,
            filters
          })
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to fetch execution records';
        let errorDetails;
        
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching execution records:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  return {
    getExecutionRecords
  };
}; 